import { useEffect, useState } from "react"

import { Eye, EyeOff, FileText, Folder, Plus, Trash2, X } from "lucide-react"
import { toast } from "sonner"

import { Button } from "@/components/ui/button"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { ConversationItem } from "@/components/sidebar/ConversationItem"
import { loadProjectDetail } from "@/hooks/useProjects"
import { setProjectReferenceHidden } from "@/lib/api"
import { useChatDropTarget } from "@/lib/chat-dnd"
import type { Conversation, Project, ProjectReference } from "@/types/api"

const MAX_PROJECTS = 100
const MAX_NAME = 80
const MAX_INSTRUCTIONS = 8000

type FormMode = { kind: "create" } | { kind: "edit"; project: Project }

export interface ProjectManagerProps {
  projects: Project[]
  loading: boolean
  activeProjectId: string | null
  conversations: Conversation[]
  activeId: string | null
  onCreate: (name: string, description: string) => Promise<Project>
  onUpdate: (id: string, body: { name?: string; description?: string; instructions?: string }) => Promise<Project>
  onDelete: (id: string, disposition: "move_to_regular" | "delete_chats") => Promise<void>
  onNewChat: (projectId: string) => void
  onOpenConversation: (id: string) => void
  onDeleteConversation: (id: string) => void
  onMoveConversation: (conversationId: string, projectId: string | null) => void
  onReferencesChanged: () => void
}

function ProjectDropZone({
  project, conversations, onMove, children,
}: {
  project: Project
  conversations: Conversation[]
  onMove: (conversationId: string, projectId: string | null) => void
  children: React.ReactNode
}) {
  const { over, props } = useChatDropTarget((conversationId) => {
    const current = conversations.find((item) => item.id === conversationId)
    if (current && current.project_id !== project.id) onMove(conversationId, project.id)
  })
  return <div {...props} className={`rounded-md ${over ? "ring-2 ring-orange bg-orange-tint" : ""}`}>{children}</div>
}

const PREVIEW_COUNT = 3

function ProjectDocuments({
  projectId, referenceCount, hiddenCount, onChanged,
}: { projectId: string; referenceCount: number; hiddenCount: number; onChanged: () => void }) {
  const [references, setReferences] = useState<ProjectReference[] | null>(null)
  const [showAll, setShowAll] = useState(false)
  const [showHidden, setShowHidden] = useState(false)
  const total = referenceCount + hiddenCount

  useEffect(() => {
    if (total === 0) {
      setReferences([])
      return
    }
    let cancelled = false
    loadProjectDetail(projectId)
      .then((detail) => { if (!cancelled) setReferences(detail.references) })
      .catch(() => { if (!cancelled) setReferences([]) })
    return () => { cancelled = true }
  }, [projectId, total, referenceCount])

  const setHidden = async (reference: ProjectReference, hidden: boolean) => {
    const apply = (value: boolean) =>
      setReferences((current) => current?.map((item) => (item.id === reference.id ? { ...item, hidden: value } : item)) ?? current)
    apply(hidden)
    try {
      await setProjectReferenceHidden(projectId, reference.id, hidden)
      onChanged()
    } catch {
      apply(!hidden)
      toast.error(hidden ? "Document could not be hidden." : "Document could not be restored.")
    }
  }

  if (!references) return <p className="px-2 py-1 text-xs text-fg4">Loading documents…</p>
  const active = references.filter((reference) => !reference.hidden)
  const hiddenRefs = references.filter((reference) => reference.hidden)
  if (active.length === 0 && hiddenRefs.length === 0) {
    return <p className="px-2 py-1 text-xs text-fg4">No referenced documents yet.</p>
  }

  const shown = showAll ? active : active.slice(0, PREVIEW_COUNT)
  const more = active.length - shown.length
  const row = (reference: ProjectReference, hidden: boolean) => (
    <div key={reference.id} className="group/doc flex items-center rounded hover:bg-hover">
      <a
        href={reference.url}
        target="_blank"
        rel="noopener noreferrer"
        title={reference.title}
        className={`flex min-w-0 flex-1 items-center gap-1.5 px-2 py-1 text-xs hover:text-foreground ${hidden ? "text-fg4" : "text-fg3"}`}
      >
        <FileText className="size-3 shrink-0" />
        <span className="min-w-0 flex-1 truncate">{reference.title}</span>
      </a>
      <button
        type="button"
        onClick={() => void setHidden(reference, !hidden)}
        aria-label={hidden ? `Restore ${reference.title}` : `Hide ${reference.title}`}
        title={hidden ? "Restore: use this document as context again" : "Hide: stop using this document as context"}
        className="mr-1 rounded p-1 text-fg4 opacity-0 hover:text-foreground group-hover/doc:opacity-100 focus-visible:opacity-100"
      >
        {hidden ? <Eye className="size-3" /> : <EyeOff className="size-3" />}
      </button>
    </div>
  )

  return (
    <div className="pb-1">
      {active.length > 0 ? (
        <>
          <div className="px-2 pt-1 pb-0.5 text-[10px] font-bold tracking-[0.09em] text-fg4 uppercase">
            Documents ({active.length})
          </div>
          {shown.map((reference) => row(reference, false))}
          {(more > 0 || showAll) && active.length > PREVIEW_COUNT && (
            <button
              type="button"
              onClick={() => setShowAll((current) => !current)}
              className="px-2 py-0.5 text-[11px] text-fg4 hover:text-foreground"
            >
              {showAll ? "Show less" : `+${more} more`}
            </button>
          )}
        </>
      ) : (
        <p className="px-2 py-1 text-xs text-fg4">All documents are hidden.</p>
      )}
      {hiddenRefs.length > 0 && (
        <div>
          <button
            type="button"
            onClick={() => setShowHidden((current) => !current)}
            aria-expanded={showHidden}
            className="px-2 pt-1 pb-0.5 text-[10px] font-bold tracking-[0.09em] text-fg4 uppercase hover:text-foreground"
          >
            Hidden ({hiddenRefs.length}) {showHidden ? "−" : "+"}
          </button>
          {showHidden && hiddenRefs.map((reference) => row(reference, true))}
        </div>
      )}
    </div>
  )
}

export function ProjectManager({
  projects, loading, activeProjectId, conversations, activeId, onCreate, onUpdate, onDelete, onNewChat, onOpenConversation, onDeleteConversation, onMoveConversation, onReferencesChanged,
}: ProjectManagerProps) {
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set(activeProjectId ? [activeProjectId] : []))
  const [formMode, setFormMode] = useState<FormMode | null>(null)
  const [busy, setBusy] = useState(false)
  const [name, setName] = useState("")
  const [description, setDescription] = useState("")
  const [instructions, setInstructions] = useState("")
  const [deleteChats, setDeleteChats] = useState(false)
  const [deleteConfirmed, setDeleteConfirmed] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  const toggle = (projectId: string) =>
    setExpanded((current) => {
      const next = new Set(current)
      if (!next.delete(projectId)) next.add(projectId)
      return next
    })

  useEffect(() => {
    if (!activeProjectId) return
    setExpanded((current) => (current.has(activeProjectId) ? current : new Set(current).add(activeProjectId)))
  }, [activeProjectId])

  const startCreate = () => {
    setName(""); setDescription(""); setInstructions("")
    setFormMode({ kind: "create" })
  }

  const startEdit = async (project: Project) => {
    setName(project.name); setDescription(project.description ?? ""); setInstructions("")
    setDeleteChats(false); setDeleteConfirmed(false); setFormMode({ kind: "edit", project })
    try {
      const result = await loadProjectDetail(project.id)
      setInstructions(result.instructions ?? "")
    } catch {
      toast.error("Project details could not be loaded.")
    }
  }

  const save = async (event: React.FormEvent) => {
    event.preventDefault()
    const cleanName = name.trim()
    if (!cleanName || cleanName.length > MAX_NAME || instructions.length > MAX_INSTRUCTIONS) return
    setBusy(true)
    try {
      if (formMode?.kind === "create") {
        const created = await onCreate(cleanName, description.trim())
        if (instructions.trim()) {
          try {
            await onUpdate(created.id, { instructions: instructions.trim() })
          } catch {
            toast.error("Project created, but its instructions could not be saved. Edit the project to retry.")
          }
        }
      } else if (formMode?.kind === "edit") {
        await onUpdate(formMode.project.id, { name: cleanName, description: description.trim(), instructions })
      }
      setFormMode(null)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Project could not be saved.")
    } finally { setBusy(false) }
  }

  const confirmDelete = async () => {
    if (formMode?.kind !== "edit") return
    const project = formMode.project
    if (deleteChats && !deleteConfirmed) return
    setBusy(true)
    try {
      await onDelete(project.id, deleteChats ? "delete_chats" : "move_to_regular")
      setFormMode(null); setDeleteDialogOpen(false)
      setExpanded((current) => { const next = new Set(current); next.delete(project.id); return next })
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Project could not be deleted.")
    } finally { setBusy(false) }
  }

  const editProject = formMode?.kind === "edit" ? formMode.project : null

  return (
    <section className="px-3 pb-3" aria-label="Projects">
      <div className="mb-2 flex items-center justify-between px-1">
        <div className="text-[11px] font-bold tracking-[0.09em] text-fg3 uppercase">Projects</div>
        <button type="button" className="rounded p-1 text-fg3 hover:bg-hover hover:text-foreground disabled:opacity-40" aria-label="Create project" title="Create project" disabled={projects.length >= MAX_PROJECTS} onClick={startCreate}>
          <Plus className="size-4" />
        </button>
      </div>

      {loading ? <p className="px-2 py-2 text-xs text-fg3">Loading projects…</p> : projects.length === 0 ? (
        <button type="button" onClick={startCreate} className="w-full rounded-md px-2 py-2 text-left text-xs text-fg3 hover:bg-hover">Create a project to group related chats.</button>
      ) : (
        <div className="space-y-1">
          {projects.map((project) => {
            const projectChats = conversations.filter((conversation) => conversation.project_id === project.id)
            const isOpen = expanded.has(project.id)
            return (
              <ProjectDropZone key={project.id} project={project} conversations={conversations} onMove={(id, target) => void onMoveConversation(id, target)}>
                <div className={`group/project flex items-center gap-1 rounded-md ${activeProjectId === project.id ? "bg-orange-tint" : ""}`}>
                  <button type="button" aria-expanded={isOpen} onClick={() => toggle(project.id)} className="flex min-w-0 flex-1 items-center gap-2 rounded-md px-2 py-2 text-left text-[13px] text-fg2 hover:bg-hover">
                    <Folder className="size-3.5 shrink-0 text-orange-press" />
                    <span className="min-w-0 flex-1 truncate">{project.name}</span>
                    <span className="text-[11px] text-fg4">{projectChats.length}</span>
                  </button>
                  <button type="button" aria-label={`New chat in ${project.name}`} title="New chat" onClick={() => onNewChat(project.id)} className="rounded p-1.5 text-fg3 hover:bg-hover hover:text-foreground">
                    <Plus className="size-3.5" />
                  </button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button type="button" aria-label={`Project actions for ${project.name}`} title="Project actions" onClick={(event) => event.stopPropagation()} className="rounded p-1.5 text-fg3 opacity-0 hover:bg-hover hover:text-foreground group-hover/project:opacity-100 focus-visible:opacity-100">•••</button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onSelect={() => void startEdit(project)}>Edit project</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                {isOpen && (
                  <div className="mt-0.5 ml-3">
                    <ProjectDocuments
                      projectId={project.id}
                      referenceCount={project.reference_count}
                      hiddenCount={project.hidden_count ?? 0}
                      onChanged={onReferencesChanged}
                    />
                    {projectChats.length > 0 ? projectChats.map((conversation) => (
                      <ConversationItem
                        key={conversation.id}
                        conversation={conversation}
                        active={conversation.id === activeId}
                        onSelect={() => onOpenConversation(conversation.id)}
                        onDelete={() => onDeleteConversation(conversation.id)}
                        projects={projects}
                        onMoveToProject={(projectId) => void onMoveConversation(conversation.id, projectId)}
                      />
                    )) : <p className="px-2 py-1 text-xs text-fg4">No conversations in this project yet.</p>}
                  </div>
                )}
              </ProjectDropZone>
            )
          })}
        </div>
      )}

      {formMode && <div role="dialog" aria-modal="true" aria-labelledby="project-dialog-title" className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4">
        <form onSubmit={(event) => void save(event)} className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl border border-border bg-card p-5 shadow-xl">
          <div className="mb-4 flex items-start justify-between gap-3">
            <div><h2 id="project-dialog-title" className="text-lg font-semibold">{formMode.kind === "create" ? "Create project" : "Edit project"}</h2><p className="mt-1 text-sm text-fg3">Projects and their conversations are private to you.</p></div>
            <button type="button" aria-label="Close" onClick={() => setFormMode(null)} className="rounded p-1 text-fg3 hover:bg-hover"><X className="size-4" /></button>
          </div>
          <label className="mb-3 block text-sm font-medium">Project name
            <input required autoFocus maxLength={MAX_NAME} value={name} onChange={(event) => setName(event.target.value)} className="mt-1.5 h-10 w-full rounded-md border border-input bg-background px-3 text-sm outline-none focus:border-orange" />
            <span className="mt-1 block text-right text-xs text-fg4">{name.length}/{MAX_NAME}</span>
          </label>
          <label className="mb-3 block text-sm font-medium">Description <span className="font-normal text-fg4">(optional)</span>
            <textarea rows={2} value={description} onChange={(event) => setDescription(event.target.value)} className="mt-1.5 w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus:border-orange" />
          </label>
          <label className="mb-4 block text-sm font-medium">Instructions <span className="font-normal text-fg4">(optional)</span>
            <textarea rows={5} maxLength={MAX_INSTRUCTIONS} value={instructions} onChange={(event) => setInstructions(event.target.value)} placeholder="Add project-specific context and guidance for your chats." className="mt-1.5 w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus:border-orange" />
            <span className="mt-1 block text-right text-xs text-fg4">{instructions.length}/{MAX_INSTRUCTIONS}</span>
          </label>
          <div className="flex flex-wrap items-center justify-end gap-2">
            {formMode.kind === "edit" && <Button type="button" variant="destructive" size="sm" className="mr-auto" onClick={() => { setDeleteChats(false); setDeleteConfirmed(false); setDeleteDialogOpen(true) }}><Trash2 className="size-3.5" /> Delete project</Button>}
            <Button type="button" variant="outline" size="sm" onClick={() => setFormMode(null)}>Cancel</Button>
            <Button type="submit" size="sm" disabled={busy || !name.trim() || name.trim().length > MAX_NAME || instructions.length > MAX_INSTRUCTIONS}>{busy ? "Saving…" : "Save project"}</Button>
          </div>
        </form>
      </div>}

      {editProject && deleteDialogOpen && <div role="dialog" aria-modal="true" aria-labelledby="project-delete-title" className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4">
        <div className="w-full max-w-md rounded-xl border border-border bg-card p-5 text-foreground shadow-xl">
          <h3 id="project-delete-title" className="text-lg font-semibold">Delete “{editProject.name}”?</h3>
          <p className="mt-2 text-sm text-fg3">Choose what happens to its conversations.</p>
          <label className="mt-4 flex items-start gap-2 text-sm"><input type="radio" checked={!deleteChats} onChange={() => { setDeleteChats(false); setDeleteConfirmed(false) }} /><span>Move conversations to regular chats</span></label>
          <label className="mt-3 flex items-start gap-2 text-sm"><input type="radio" checked={deleteChats} onChange={() => setDeleteChats(true)} /><span>Delete all project conversations</span></label>
          {deleteChats && <label className="mt-3 flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm"><input type="checkbox" checked={deleteConfirmed} onChange={(event) => setDeleteConfirmed(event.target.checked)} /><span>I understand this permanently deletes all conversations in this project.</span></label>}
          <div className="mt-5 flex justify-end gap-2"><Button type="button" variant="outline" size="sm" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button><Button type="button" variant="destructive" size="sm" disabled={busy || (deleteChats && !deleteConfirmed)} onClick={() => void confirmDelete()}>{busy ? "Deleting…" : deleteChats ? "Delete project and chats" : "Delete project"}</Button></div>
        </div>
      </div>}
    </section>
  )
}
